import React, { useState, useEffect } from 'react';
import {
  adminGetBlog,
  adminCreateBlog,
  adminUpdateBlog,
  adminGetLanguages,
} from '../../services/blogApi';
import { BlogBlock, BlogLanguage } from '../../types/blog';

// Extracts the real image URL from Google imgres links.
// Also handles other common redirect/viewer patterns.
function extractDirectImageUrl(raw: string): string {
  try {
    const u = new URL(raw);
    // Google imgres: ?imgurl=<encoded direct url>
    if (u.hostname.includes('google.com') && u.pathname === '/imgres') {
      const imgurl = u.searchParams.get('imgurl');
      if (imgurl) return decodeURIComponent(imgurl);
    }
    // Google image proxy
    if (u.hostname === 'images.google.com') {
      const imgurl = u.searchParams.get('imgurl');
      if (imgurl) return decodeURIComponent(imgurl);
    }
  } catch {
    // not a valid URL, return as-is
  }
  return raw;
}

interface Props {
  secret: string;
  blogId: string | null;
  onDone: () => void;
}

type BlockType = BlogBlock['type'];

const BLOCK_TYPES: { value: BlockType; label: string }[] = [
  { value: 'heading', label: 'Heading' },
  { value: 'paragraph', label: 'Paragraph' },
  { value: 'image', label: 'Image' },
  { value: 'image_grid', label: 'Image Grid' },
  { value: 'bullet_list', label: 'Bullet List' },
  { value: 'numbered_list', label: 'Numbered List' },
  { value: 'quote', label: 'Quote' },
  { value: 'callout', label: 'Callout' },
  { value: 'divider', label: 'Divider' },
  { value: 'video_embed', label: 'Video Embed' },
  { value: 'stat_highlight', label: 'Stat Highlight' },
  { value: 'recipe_card', label: 'Recipe Card' },
];

function newBlock(type: BlockType, order: number): BlogBlock {
  const base = { type, order };
  switch (type) {
    case 'heading': return { ...base, level: 2, content: '' };
    case 'paragraph': return { ...base, content: '' };
    case 'image': return { ...base, url: '', caption: '', alt: '' };
    case 'image_grid': return { ...base, images: [{ url: '', caption: '' }] };
    case 'bullet_list': return { ...base, items: [''] };
    case 'numbered_list': return { ...base, items: [''] };
    case 'quote': return { ...base, content: '', author: '' };
    case 'callout': return { ...base, variant: 'tip', content: '' };
    case 'divider': return { ...base };
    case 'video_embed': return { ...base, url: '', caption: '' };
    case 'stat_highlight': return { ...base, stats: [{ value: '', label: '' }] };
    case 'recipe_card': return { ...base, ingredients: [''], steps: [''] };
    default: return { ...base, content: '' };
  }
}

const inp: React.CSSProperties = {
  width: '100%',
  padding: '0.55rem 0.8rem',
  borderRadius: '8px',
  border: '1px solid #e5e7eb',
  fontSize: '0.9rem',
  fontFamily: 'inherit',
  boxSizing: 'border-box',
  outline: 'none',
};
const ta: React.CSSProperties = { ...inp, resize: 'vertical', minHeight: '90px' };
const label: React.CSSProperties = {
  fontSize: '0.78rem',
  fontWeight: 600,
  color: '#6b6b8a',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  marginBottom: '0.3rem',
  display: 'block',
};
const field: React.CSSProperties = { marginBottom: '0.75rem' };
const row: React.CSSProperties = { display: 'flex', gap: '0.75rem', marginBottom: '0.75rem', flexWrap: 'wrap' };
const smallBtn: React.CSSProperties = {
  padding: '0.25rem 0.6rem',
  borderRadius: '6px',
  border: '1px solid #e5e7eb',
  background: '#fff',
  cursor: 'pointer',
  fontSize: '0.8rem',
  fontFamily: 'inherit',
};

const AdminBlogEditor: React.FC<Props> = ({ secret, blogId, onDone }) => {
  const isEdit = !!blogId;

  const [languages, setLanguages] = useState<BlogLanguage[]>([]);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Meta fields
  const [langCode, setLangCode] = useState('en');
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [summary, setSummary] = useState('');
  const [coverUrl, setCoverUrl] = useState('');
  const [author, setAuthor] = useState('');
  const [tags, setTags] = useState('');
  const [groupId, setGroupId] = useState('');
  const [status, setStatus] = useState<'draft' | 'published'>('draft');

  // Blocks
  const [blocks, setBlocks] = useState<BlogBlock[]>([]);
  const [newBlockType, setNewBlockType] = useState<BlockType>('paragraph');

  useEffect(() => {
    adminGetLanguages(secret)
      .then((res) => setLanguages(res.data ?? []))
      .catch(() => {});
  }, [secret]);

  useEffect(() => {
    if (!isEdit || !blogId) return;
    setLoading(true);
    adminGetBlog(secret, blogId)
      .then((res) => {
        const d = res.data ?? res;
        setLangCode(d.language_code ?? 'en');
        setTitle(d.title ?? '');
        setSlug(d.slug ?? '');
        setSummary(d.summary ?? '');
        setCoverUrl(d.cover_image_url ?? '');
        setAuthor(d.author ?? '');
        setTags((d.tags ?? []).join(', '));
        setGroupId(d.group_id ?? '');
        setStatus(d.status ?? 'draft');
        setBlocks(d.blocks ?? []);
      })
      .catch(() => setError('Failed to load blog.'))
      .finally(() => setLoading(false));
  }, [secret, blogId, isEdit]);

  // Auto-generate slug from title on create
  const handleTitleChange = (val: string) => {
    setTitle(val);
    if (!isEdit) {
      setSlug(
        val
          .toLowerCase()
          .trim()
          .replace(/[^a-z0-9\s-]/g, '')
          .replace(/\s+/g, '-')
          .slice(0, 80)
      );
    }
  };

  // ── Block mutations ──────────────────────────────────────────────────────

  const addBlock = () => {
    const order = blocks.length > 0 ? Math.max(...blocks.map((b) => b.order)) + 1 : 1;
    setBlocks((prev) => [...prev, newBlock(newBlockType, order)]);
  };

  const removeBlock = (idx: number) => {
    setBlocks((prev) => prev.filter((_, i) => i !== idx));
  };

  const moveBlock = (idx: number, dir: -1 | 1) => {
    const next = idx + dir;
    if (next < 0 || next >= blocks.length) return;
    setBlocks((prev) => {
      const arr = [...prev];
      [arr[idx], arr[next]] = [arr[next], arr[idx]];
      // Reorder
      return arr.map((b, i) => ({ ...b, order: i + 1 }));
    });
  };

  const updateBlock = (idx: number, patch: Partial<BlogBlock>) => {
    setBlocks((prev) => prev.map((b, i) => (i === idx ? { ...b, ...patch } : b)));
  };

  // ── Save ─────────────────────────────────────────────────────────────────

  const sanitizeBlocks = (raw: BlogBlock[]): { blocks: BlogBlock[]; warnings: string[] } => {
    const warnings: string[] = [];
    const cleaned: BlogBlock[] = [];

    raw.forEach((block, i) => {
      const order = i + 1;

      switch (block.type) {
        case 'heading':
        case 'paragraph': {
          if (!block.content?.trim()) { warnings.push(`Block ${order} (${block.type}) is empty — skipped.`); return; }
          cleaned.push({ ...block, order, content: block.content.trim() });
          break;
        }
        case 'image': {
          if (!block.url?.trim()) { warnings.push(`Block ${order} (image) has no URL — skipped.`); return; }
          cleaned.push({ ...block, order, url: block.url.trim() });
          break;
        }
        case 'image_grid': {
          const imgs = (block.images ?? []).filter((img) => img.url?.trim());
          if (imgs.length < 2) { warnings.push(`Block ${order} (image_grid) needs at least 2 images — skipped.`); return; }
          cleaned.push({ ...block, order, images: imgs });
          break;
        }
        case 'bullet_list':
        case 'numbered_list': {
          const items = (block.items ?? []).map((s) => s.trim()).filter(Boolean);
          if (items.length === 0) { warnings.push(`Block ${order} (${block.type}) has no items — skipped.`); return; }
          cleaned.push({ ...block, order, items });
          break;
        }
        case 'quote': {
          if (!block.content?.trim()) { warnings.push(`Block ${order} (quote) is empty — skipped.`); return; }
          cleaned.push({ ...block, order, content: block.content.trim(), author: block.author?.trim() || undefined });
          break;
        }
        case 'callout': {
          if (!block.content?.trim()) { warnings.push(`Block ${order} (callout) is empty — skipped.`); return; }
          cleaned.push({ ...block, order, content: block.content.trim() });
          break;
        }
        case 'divider': {
          cleaned.push({ ...block, order });
          break;
        }
        case 'video_embed': {
          if (!block.url?.trim()) { warnings.push(`Block ${order} (video_embed) has no URL — skipped.`); return; }
          cleaned.push({ ...block, order, url: block.url.trim() });
          break;
        }
        case 'stat_highlight': {
          const stats = (block.stats ?? []).filter((s) => s.value?.trim() && s.label?.trim());
          if (stats.length === 0) { warnings.push(`Block ${order} (stat_highlight) has no stats — skipped.`); return; }
          cleaned.push({ ...block, order, stats });
          break;
        }
        case 'recipe_card': {
          const ingredients = (block.ingredients ?? []).map((s) => s.trim()).filter(Boolean);
          const steps = (block.steps ?? []).map((s) => s.trim()).filter(Boolean);
          if (ingredients.length === 0 && steps.length === 0) { warnings.push(`Block ${order} (recipe_card) is empty — skipped.`); return; }
          cleaned.push({ ...block, order, ingredients, steps });
          break;
        }
        default:
          cleaned.push({ ...block, order });
      }
    });

    return { blocks: cleaned, warnings };
  };

  const handleSave = async () => {
    if (!title.trim()) { setError('Title is required.'); return; }
    if (!slug.trim()) { setError('Slug is required.'); return; }
    if (!langCode) { setError('Language is required.'); return; }

    const { blocks: cleanBlocks, warnings } = sanitizeBlocks(blocks);

    if (warnings.length > 0) {
      const proceed = window.confirm(
        `Some blocks were skipped:\n\n${warnings.join('\n')}\n\nContinue saving without them?`
      );
      if (!proceed) return;
    }

    setSaving(true);
    setError('');

    const payload = {
      language_code: langCode,
      title: title.trim(),
      slug: slug.trim(),
      group_id: groupId.trim() || undefined,
      summary: summary.trim(),
      cover_image_url: coverUrl.trim(),
      author: author.trim(),
      tags: tags.split(',').map((t) => t.trim()).filter(Boolean),
      status,
      blocks: cleanBlocks,
    };

    try {
      if (isEdit && blogId) {
        await adminUpdateBlog(secret, blogId, payload);
      } else {
        await adminCreateBlog(secret, payload);
      }
      onDone();
    } catch (e: any) {
      setError(e?.message ?? 'Failed to save blog.');
    } finally {
      setSaving(false);
    }
  };

  // ── Block field renderer ─────────────────────────────────────────────────

  const renderBlockFields = (block: BlogBlock, idx: number) => {
    const up = (patch: Partial<BlogBlock>) => updateBlock(idx, patch);

    switch (block.type) {
      case 'heading':
        return (
          <div style={row}>
            <div style={{ flex: '0 0 100px' }}>
              <label style={label}>Level</label>
              <select
                style={inp}
                value={block.level ?? 2}
                onChange={(e) => up({ level: Number(e.target.value) as 1 | 2 | 3 })}
              >
                <option value={1}>H1</option>
                <option value={2}>H2</option>
                <option value={3}>H3</option>
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label style={label}>Content</label>
              <input
                style={inp}
                value={block.content ?? ''}
                onChange={(e) => up({ content: e.target.value })}
                placeholder="Heading text..."
              />
            </div>
          </div>
        );

      case 'paragraph':
        return (
          <div style={field}>
            <label style={label}>Content (HTML supported)</label>
            <textarea
              style={ta}
              value={block.content ?? ''}
              onChange={(e) => up({ content: e.target.value })}
              placeholder="Write paragraph content..."
            />
          </div>
        );

      case 'image':
        return (
          <>
            <div style={field}><label style={label}>Image URL</label>
              <input style={inp} value={block.url ?? ''} onChange={(e) => up({ url: extractDirectImageUrl(e.target.value) })} placeholder="https://..." />
            </div>
            <div style={row}>
              <div style={{ flex: 1 }}><label style={label}>Caption</label>
                <input style={inp} value={block.caption ?? ''} onChange={(e) => up({ caption: e.target.value })} placeholder="Optional caption" />
              </div>
              <div style={{ flex: 1 }}><label style={label}>Alt text</label>
                <input style={inp} value={block.alt ?? ''} onChange={(e) => up({ alt: e.target.value })} placeholder="Alt text for SEO" />
              </div>
            </div>
          </>
        );

      case 'image_grid':
        return (
          <div style={field}>
            <label style={label}>Images (one per row)</label>
            {(block.images ?? []).map((img, j) => (
              <div key={j} style={{ ...row, alignItems: 'flex-end', marginBottom: '0.5rem' }}>
                <div style={{ flex: 2 }}>
                  <input style={inp} value={img.url} onChange={(e) => {
                    const imgs = [...(block.images ?? [])];
                    imgs[j] = { ...imgs[j], url: extractDirectImageUrl(e.target.value) };
                    up({ images: imgs });
                  }} placeholder="Image URL" />
                </div>
                <div style={{ flex: 1 }}>
                  <input style={inp} value={img.caption ?? ''} onChange={(e) => {
                    const imgs = [...(block.images ?? [])];
                    imgs[j] = { ...imgs[j], caption: e.target.value };
                    up({ images: imgs });
                  }} placeholder="Caption" />
                </div>
                <button style={{ ...smallBtn, color: '#ef4444' }} onClick={() => {
                  up({ images: (block.images ?? []).filter((_, k) => k !== j) });
                }}>✕</button>
              </div>
            ))}
            <button style={smallBtn} onClick={() => up({ images: [...(block.images ?? []), { url: '' }] })}>
              + Add image
            </button>
          </div>
        );

      case 'bullet_list':
      case 'numbered_list':
        return (
          <div style={field}>
            <label style={label}>Items (one per line)</label>
            <textarea
              style={ta}
              value={(block.items ?? []).join('\n')}
              onChange={(e) => up({ items: e.target.value.split('\n') })}
              placeholder="Item 1&#10;Item 2&#10;Item 3"
            />
          </div>
        );

      case 'quote':
        return (
          <>
            <div style={field}><label style={label}>Quote text</label>
              <textarea style={ta} value={block.content ?? ''} onChange={(e) => up({ content: e.target.value })} placeholder="Quote..." />
            </div>
            <div style={field}><label style={label}>Author (optional)</label>
              <input style={inp} value={block.author ?? ''} onChange={(e) => up({ author: e.target.value })} placeholder="Author name" />
            </div>
          </>
        );

      case 'callout':
        return (
          <>
            <div style={field}><label style={label}>Variant</label>
              <select style={inp} value={block.variant ?? 'tip'} onChange={(e) => up({ variant: e.target.value as any })}>
                <option value="tip">💡 Tip</option>
                <option value="info">ℹ️ Info</option>
                <option value="warning">⚠️ Warning</option>
              </select>
            </div>
            <div style={field}><label style={label}>Content</label>
              <textarea style={ta} value={block.content ?? ''} onChange={(e) => up({ content: e.target.value })} placeholder="Callout message..." />
            </div>
          </>
        );

      case 'divider':
        return (
          <div style={{ borderTop: '2px dashed #e5e7eb', margin: '0.5rem 0', borderRadius: '4px' }} />
        );

      case 'video_embed':
        return (
          <>
            <div style={field}><label style={label}>Video URL (YouTube or direct)</label>
              <input style={inp} value={block.url ?? ''} onChange={(e) => up({ url: e.target.value })} placeholder="https://youtube.com/watch?v=..." />
            </div>
            <div style={field}><label style={label}>Caption (optional)</label>
              <input style={inp} value={block.caption ?? ''} onChange={(e) => up({ caption: e.target.value })} placeholder="Caption..." />
            </div>
          </>
        );

      case 'stat_highlight':
        return (
          <div style={field}>
            <label style={label}>Stats</label>
            {(block.stats ?? []).map((stat, j) => (
              <div key={j} style={{ ...row, alignItems: 'flex-end', marginBottom: '0.5rem' }}>
                <div style={{ flex: 1 }}>
                  <input style={inp} value={stat.value} onChange={(e) => {
                    const stats = [...(block.stats ?? [])];
                    stats[j] = { ...stats[j], value: e.target.value };
                    up({ stats });
                  }} placeholder="Value (e.g. 10 min)" />
                </div>
                <div style={{ flex: 1 }}>
                  <input style={inp} value={stat.label} onChange={(e) => {
                    const stats = [...(block.stats ?? [])];
                    stats[j] = { ...stats[j], label: e.target.value };
                    up({ stats });
                  }} placeholder="Label (e.g. Prep time)" />
                </div>
                <button style={{ ...smallBtn, color: '#ef4444' }} onClick={() => {
                  up({ stats: (block.stats ?? []).filter((_, k) => k !== j) });
                }}>✕</button>
              </div>
            ))}
            <button style={smallBtn} onClick={() => up({ stats: [...(block.stats ?? []), { value: '', label: '' }] })}>
              + Add stat
            </button>
          </div>
        );

      case 'recipe_card':
        return (
          <div style={row}>
            <div style={{ flex: 1 }}>
              <label style={label}>Ingredients (one per line)</label>
              <textarea style={ta} value={(block.ingredients ?? []).join('\n')} onChange={(e) => up({ ingredients: e.target.value.split('\n') })} placeholder="2 eggs&#10;50g cheese" />
            </div>
            <div style={{ flex: 1 }}>
              <label style={label}>Steps (one per line)</label>
              <textarea style={ta} value={(block.steps ?? []).join('\n')} onChange={(e) => up({ steps: e.target.value.split('\n') })} placeholder="Crack eggs&#10;Add cheese" />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return <div style={{ padding: '3rem', textAlign: 'center', color: '#6b6b8a' }}>Loading blog...</div>;
  }

  const CARD: React.CSSProperties = {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '12px',
    padding: '1.25rem',
    marginBottom: '1.5rem',
    boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
  };

  return (
    <div style={{ maxWidth: '860px', margin: '0 auto' }}>
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
        <button onClick={onDone} style={{ ...smallBtn, fontSize: '0.9rem' }}>← Back</button>
        <h2 style={{ fontSize: '1.3rem', fontWeight: 700, color: '#07091A', margin: 0 }}>
          {isEdit ? 'Edit Blog' : 'New Blog'}
        </h2>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem' }}>
          <select
            style={{ ...inp, width: 'auto', padding: '0.4rem 0.75rem' }}
            value={status}
            onChange={(e) => setStatus(e.target.value as any)}
          >
            <option value="draft">Draft</option>
            <option value="published">Published</option>
          </select>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              padding: '0.45rem 1.25rem',
              background: 'linear-gradient(135deg, #2DD4BF 0%, #4C82FF 100%)',
              color: '#fff',
              border: 'none',
              borderRadius: '10px',
              fontWeight: 700,
              cursor: saving ? 'not-allowed' : 'pointer',
              fontSize: '0.9rem',
              fontFamily: 'inherit',
              opacity: saving ? 0.7 : 1,
            }}
          >
            {saving ? 'Saving...' : isEdit ? 'Save changes' : 'Create blog'}
          </button>
        </div>
      </div>

      {error && (
        <div style={{ background: '#fee2e2', color: '#ef4444', padding: '0.75rem 1rem', borderRadius: '10px', marginBottom: '1rem', fontSize: '0.9rem' }}>
          {error}
        </div>
      )}

      {/* Meta card */}
      <div style={CARD}>
        <p style={{ fontWeight: 700, fontSize: '0.9rem', color: '#07091A', marginBottom: '1rem' }}>Blog Info</p>
        <div style={row}>
          <div style={{ flex: '0 0 160px' }}>
            <label style={label}>Language *</label>
            <select style={inp} value={langCode} onChange={(e) => setLangCode(e.target.value)}>
              {languages.map((l) => (
                <option key={l.code} value={l.code}>{l.name} ({l.code})</option>
              ))}
              {languages.length === 0 && <option value="en">English (en)</option>}
            </select>
          </div>
          <div style={{ flex: 1 }}>
            <label style={label}>Title *</label>
            <input style={inp} value={title} onChange={(e) => handleTitleChange(e.target.value)} placeholder="Blog title..." />
          </div>
        </div>
        <div style={row}>
          <div style={{ flex: 1 }}>
            <label style={label}>Slug *</label>
            <input style={inp} value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="my-blog-post" />
          </div>
          <div style={{ flex: 1 }}>
            <label style={label}>Group ID <span style={{ fontWeight: 400, color: '#9ca3af' }}>(links same topic across languages)</span></label>
            <input style={inp} value={groupId} onChange={(e) => setGroupId(e.target.value)} placeholder="e.g. pasta-recipe" />
          </div>
        </div>
        <div style={field}>
          <label style={label}>Summary</label>
          <textarea style={{ ...ta, minHeight: '70px' }} value={summary} onChange={(e) => setSummary(e.target.value)} placeholder="Short description shown on list page..." />
        </div>
        <div style={row}>
          <div style={{ flex: 2 }}>
            <label style={label}>Cover Image URL</label>
            <input style={inp} value={coverUrl} onChange={(e) => setCoverUrl(extractDirectImageUrl(e.target.value))} placeholder="https://..." />
          </div>
          <div style={{ flex: 1 }}>
            <label style={label}>Author</label>
            <input style={inp} value={author} onChange={(e) => setAuthor(e.target.value)} placeholder="Cook AI Team" />
          </div>
        </div>
        <div style={field}>
          <label style={label}>Tags (comma separated)</label>
          <input style={inp} value={tags} onChange={(e) => setTags(e.target.value)} placeholder="breakfast, easy, vegetarian" />
        </div>
      </div>

      {/* Blocks */}
      <div style={CARD}>
        <p style={{ fontWeight: 700, fontSize: '0.9rem', color: '#07091A', marginBottom: '1rem' }}>
          Content Blocks ({blocks.length})
        </p>

        {blocks.map((block, idx) => (
          <div
            key={idx}
            style={{
              border: '1px solid #e5e7eb',
              borderRadius: '10px',
              padding: '1rem',
              marginBottom: '0.75rem',
              background: '#fafafa',
            }}
          >
            {/* Block header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
              <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#6b6b8a', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                {block.type.replace(/_/g, ' ')}
              </span>
              <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.3rem' }}>
                <button style={smallBtn} onClick={() => moveBlock(idx, -1)} title="Move up">↑</button>
                <button style={smallBtn} onClick={() => moveBlock(idx, 1)} title="Move down">↓</button>
                <button style={{ ...smallBtn, color: '#ef4444', borderColor: '#fee2e2' }} onClick={() => removeBlock(idx)}>✕</button>
              </div>
            </div>

            {renderBlockFields(block, idx)}
          </div>
        ))}

        {/* Add block */}
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginTop: '0.5rem' }}>
          <select
            style={{ ...inp, flex: 1 }}
            value={newBlockType}
            onChange={(e) => setNewBlockType(e.target.value as BlockType)}
          >
            {BLOCK_TYPES.map((bt) => (
              <option key={bt.value} value={bt.value}>{bt.label}</option>
            ))}
          </select>
          <button
            onClick={addBlock}
            style={{
              padding: '0.55rem 1.25rem',
              background: 'linear-gradient(135deg, #2DD4BF 0%, #4C82FF 100%)',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              fontWeight: 600,
              cursor: 'pointer',
              fontSize: '0.9rem',
              fontFamily: 'inherit',
              whiteSpace: 'nowrap',
            }}
          >
            + Add Block
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminBlogEditor;
